import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Sidebar from '../components/Sidebar';

export default function AdminDomainSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [settings, setSettings] = useState({
    markupPercent: 0,
    markupFixed: 0
  });

  const [preview, setPreview] = useState({
    com: 0,
    io: 0,
    tv: 0
  });

  // Sample wholesale prices for preview
  const WHOLESALE_PRICES = {
    com: 8.99,
    io: 48.99,
    tv: 36.99
  };

  // Load current settings
  useEffect(() => {
    loadSettings();
  }, []);

  // Update price previews when settings change
  useEffect(() => {
    calculatePreviews();
  }, [settings]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/admin/domain-settings');
      setSettings({
        markupPercent: response.data.markupPercent,
        markupFixed: response.data.markupFixed
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load settings');
      console.error('Error loading settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculatePreviews = () => {
    const newPreview = {};
    Object.entries(WHOLESALE_PRICES).forEach(([tld, wholesale]) => {
      const percentAdjustment = wholesale * (settings.markupPercent / 100);
      const retail = parseFloat((wholesale + percentAdjustment + settings.markupFixed).toFixed(2));
      newPreview[tld] = retail;
    });
    setPreview(newPreview);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const numValue = value === '' ? 0 : parseFloat(value);
    
    if (!isNaN(numValue) && numValue >= 0) {
      setSettings({
        ...settings,
        [name]: numValue
      });
      setError(null);
      setSuccess(null);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await api.put('/admin/domain-settings', {
        markupPercent: settings.markupPercent,
        markupFixed: settings.markupFixed
      });

      setSuccess('Domain pricing settings updated successfully!');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
      console.error('Error saving settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    loadSettings();
    setSuccess(null);
    setError(null);
  };

  const getMarginAmount = (tld) => {
    const wholesale = WHOLESALE_PRICES[tld];
    const percentAdjustment = wholesale * (settings.markupPercent / 100);
    const margin = percentAdjustment + settings.markupFixed;
    return parseFloat(margin.toFixed(2));
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 p-8 bg-gray-50">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      
      <div className="flex-1 p-8">
        <div className="max-w-4xl">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Domain Pricing Settings</h1>
            <p className="text-gray-600">Configure markup for NameCheap domain pricing</p>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Success Alert */}
          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800">{success}</p>
            </div>
          )}

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Settings Form */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Markup Configuration</h2>

              {/* Percentage Markup */}
              <div className="mb-6">
                <label htmlFor="markupPercent" className="block text-sm font-medium text-gray-700 mb-2">
                  Percentage Markup (%)
                </label>
                <div className="flex items-center">
                  <input
                    id="markupPercent"
                    name="markupPercent"
                    type="number"
                    min="0"
                    step="0.1"
                    value={settings.markupPercent || ''}
                    onChange={handleInputChange}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                  />
                  <span className="ml-2 text-gray-600">%</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">Add a percentage markup to all domains (e.g., 25 = 25% markup)</p>
              </div>

              {/* Fixed Markup */}
              <div className="mb-6">
                <label htmlFor="markupFixed" className="block text-sm font-medium text-gray-700 mb-2">
                  Fixed Markup Per Domain ($)
                </label>
                <div className="flex items-center">
                  <span className="mr-2 text-gray-600">$</span>
                  <input
                    id="markupFixed"
                    name="markupFixed"
                    type="number"
                    min="0"
                    step="0.01"
                    value={settings.markupFixed || ''}
                    onChange={handleInputChange}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Add a fixed dollar amount to every domain (e.g., 2.00 = $2.00 per domain)</p>
              </div>

              {/* Total Markup Display */}
              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <p className="text-sm text-gray-600 mb-2">Total Markup Applied:</p>
                <p className="text-2xl font-bold text-blue-600">
                  {settings.markupPercent}% + ${settings.markupFixed.toFixed(2)}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
                <button
                  onClick={handleReset}
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>

            {/* Price Preview */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Price Preview</h2>
              <p className="text-sm text-gray-600 mb-4">Sample domains with current markup settings:</p>

              <div className="space-y-4">
                {Object.entries(WHOLESALE_PRICES).map(([tld, wholesale]) => {
                  const retail = preview[tld];
                  const margin = getMarginAmount(tld);

                  return (
                    <div key={tld} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-gray-900">.{tld}</h3>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-green-600">${retail.toFixed(2)}/yr</p>
                          <p className="text-xs text-gray-500">Per year</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <p className="text-gray-500">Wholesale</p>
                          <p className="font-semibold text-gray-700">${wholesale.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">+Markup</p>
                          <p className="font-semibold text-gray-700">${margin.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Margin %</p>
                          <p className="font-semibold text-gray-700">
                            {((margin / wholesale) * 100).toFixed(1)}%
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Info Box */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-600">
                  <strong>Note:</strong> Preview shows typical TLDs. All domains use the same markup calculation.
                </p>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-8 bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">How It Works</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Percentage Markup</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Adds a percentage on top of the wholesale price. Example:
                </p>
                <div className="bg-gray-50 p-3 rounded text-sm font-mono text-gray-700">
                  .com @ $8.99 + 25% = $11.24
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Fixed Markup</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Adds a fixed amount to every domain. Example:
                </p>
                <div className="bg-gray-50 p-3 rounded text-sm font-mono text-gray-700">
                  .com @ $8.99 + $2.00 = $10.99
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Combined Effect</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Both markups are applied together. Example:
                </p>
                <div className="bg-gray-50 p-3 rounded text-sm font-mono text-gray-700">
                  .com @ $8.99 + 20% + $1.50 = $12.29
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">When It Applies</h4>
                <p className="text-sm text-gray-600">
                  • Domain search results<br/>
                  • Pricing endpoints<br/>
                  • Customer checkouts<br/>
                  • Domain renewals
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
